<script setup lang="ts">
import { computed } from 'vue'
import type { GenerationDebugStep } from '@/types/generationDebug'

const props = withDefaults(
  defineProps<{
    steps?: GenerationDebugStep[] | null
    title?: string
    emptyLabel?: string
  }>(),
  {
    title: 'Debug Steps',
    emptyLabel: 'No debug steps captured.'
  }
)

type KeyValue = {
  key: string
  value: string
}

type TextSection = {
  key: string
  label: string
  value: string
}

const normalizedSteps = computed(() => props.steps ?? [])

const normalizeText = (value?: string | null): string => value?.trim() ?? ''

const formatStepTitle = (step: GenerationDebugStep, index: number): string => {
  const stepNumber =
    step.stepIndex !== null && step.stepIndex !== undefined ? step.stepIndex : index + 1
  const type = normalizeText(step.type)
  return type ? `Step ${stepNumber}: ${type}` : `Step ${stepNumber}`
}

const buildMetadata = (step: GenerationDebugStep): KeyValue[] => {
  const entries: KeyValue[] = []
  const type = normalizeText(step.type)
  const model = normalizeText(step.model)
  const modelProvider = normalizeText(step.modelProvider)
  const modelName = normalizeText(step.modelName)

  if (type) entries.push({ key: 'type', value: type })
  if (model) entries.push({ key: 'model', value: model })
  if (modelProvider) entries.push({ key: 'provider', value: modelProvider })
  if (modelName) entries.push({ key: 'modelName', value: modelName })

  return entries
}

const buildTextSections = (step: GenerationDebugStep): TextSection[] => {
  const candidates: TextSection[] = [
    { key: 'template', label: 'Template', value: normalizeText(step.template) },
    {
      key: 'assistantPrefill',
      label: 'Chat Prefill',
      value: normalizeText(step.assistantPrefill)
    },
    { key: 'renderedPrompt', label: 'Rendered Prompt', value: normalizeText(step.renderedPrompt) },
    { key: 'result', label: 'Result', value: normalizeText(step.result) }
  ]

  return candidates.filter(section => section.value.length > 0)
}
</script>

<template>
  <section class="bg-muted/20 rounded-lg border p-3">
    <div class="mb-2 flex items-center justify-between gap-2">
      <p class="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
        {{ title }}
      </p>
      <span
        class="bg-background text-muted-foreground inline-flex min-w-6 items-center justify-center rounded border px-1.5 py-0.5 text-[11px] font-medium"
      >
        {{ normalizedSteps.length }}
      </span>
    </div>

    <p v-if="normalizedSteps.length === 0" class="text-muted-foreground text-xs italic">
      {{ emptyLabel }}
    </p>

    <div v-else class="space-y-2">
      <details
        v-for="(step, index) in normalizedSteps"
        :key="`${step.stepIndex ?? index}-${step.type ?? 'step'}`"
        class="bg-background/90 rounded border p-2"
      >
        <summary class="cursor-pointer text-sm font-medium select-none">
          {{ formatStepTitle(step, index) }}
        </summary>

        <div class="mt-2 space-y-2">
          <div v-if="buildMetadata(step).length" class="flex flex-wrap gap-1.5">
            <span
              v-for="entry in buildMetadata(step)"
              :key="`${entry.key}-${entry.value}`"
              class="bg-muted text-muted-foreground rounded border px-1.5 py-0.5 text-[11px]"
            >
              {{ entry.key }}: {{ entry.value }}
            </span>
          </div>

          <div
            v-for="section in buildTextSections(step)"
            :key="section.key"
            class="bg-muted/35 space-y-1 rounded border p-2"
          >
            <p class="text-muted-foreground text-[11px] font-semibold uppercase">
              {{ section.label }}
            </p>
            <pre class="max-h-52 overflow-y-auto text-[11px] leading-relaxed whitespace-pre-wrap">{{
              section.value
            }}</pre>
          </div>
        </div>
      </details>
    </div>
  </section>
</template>
